/**
 * @render react
 * @name Image
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';
import uniqid from 'uniqid';

import { withAmp } from 'Components/protons/withAmp';
import { withPolaris } from 'Components/protons/Polaris';

import renderAmp from './amp';

import './_style.scss';

const getClassesWithoutLazyLoad = (classes) => {
  const { lazyLoad, ...classesWithOutLazyLoad } = classes;
  return classesWithOutLazyLoad;
};

/**
 * Image component
 *
 * @param {string} src - Source of the image
 * @param {object} srcSet - Object containing the set of sources for responsive image
 * @param {string} alt - Alternative text for the image
 * @param {boolean} lazyLoad - Check to activate Lazy Load functionality
 * @param {number} lazyLoadOffset - Offset if lazyLoading is set to true
 * @param {number} width - width of the image
 * @param {number} height - height of the image
 * @param {boolean} backgroundImage - Whether we want the image to be rendered as a background image
 * @param {Function} onClick - function to be called on component click
 * @param {boolean} loadStyles - Whether to apply the class names for styling.
 * @param {Object} extraClassNames - extra classes to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 * @param {boolean} isLCPCandidate - Whether the image is a candidate for LCP. Default is false.
 */

export class Image extends Component {
  get sizes() {
    const {
      ampDisableTransforms,
      context: {
        imageSizesCallback,
        platform,
      },
      disableTransforms,
      height,
      images,
      renderPictureTag,
      size,
      sizes,
      src,
      srcSet,
      width,
      useStaticTransform,
    } = this.props;

    const withoutTransforms = !imageSizesCallback || !size || disableTransforms || (platform === 'amp' && ampDisableTransforms);

    if (withoutTransforms) {
      return {
        src,
        srcSet,
        sizes,
        width,
        height,
      };
    }

    // The responsive images
    // In case the images are passed as an object (with one or more images), we will assume that each single image
    // has its own src and size, and we will use the imageSizesCallback for each one of them
    // we will pass singleImage as true to the imageSizesCallback to avoid looping through the platforms and dpi.
    // Before rendering the image, we will sort the images by width in order to have the smallest image first in the
    // picture tag.
    if (renderPictureTag && images && typeof images === 'object' && Object.keys(images).length > 0) {
      return Object.keys(images).map((key) => {
        const image = images[key];
        return imageSizesCallback(
          image.src,
          image.size,
          image.width,
          image.height,
          true,
          useStaticTransform,
        );
      }).sort((a, b) => a.width - b.width);
    }

    return imageSizesCallback(
      src,
      size,
      width,
      height,
      false,
      useStaticTransform,
    );
  }

  get imageTag() {
    const {
      alt,
      context: {
        cssPrefix,
      },
      extraClassNames,
      isLCPCandidate,
      lazyLoad,
      loadStyles,
      onClick,
      renderPictureTag,
      role,
    } = this.props;
    const {
      src,
      srcSet,
      sizes,
      width,
      height,
    } = this.sizes;

    // pictureTagProps to be used in the picture tag for responsive images
    const pictureTagProps = this.sizes;

    // This is a unique class name used to remove from display a first <img> when JavaScript disabled.
    // https://creativesolutions.atlassian.net/browse/APP-1023 (also all Linked issues)
    // https://creativesolutions.atlassian.net/browse/APP-1565
    const imgHideOnJavaScriptDisabled = `imgHideOnJavaScriptDisabled_${uniqid()}`;
    return renderPictureTag && pictureTagProps.length > 0 && pictureTagProps[0].src ? (
      <picture>
        {pictureTagProps.map((item, index) => {
          const previousItem = this.sizes[index - 1];
          const isLastItem = index === this.sizes.length - 1;
          let media = '';

          if (previousItem && !isLastItem) {
            media = `(min-width: ${previousItem.width}px) and (max-width: ${item.width}px)`;
          } else if (previousItem && isLastItem) {
            media = `(min-width: ${previousItem.width}px)`;
          } else {
            media = `(max-width: ${item.width}px)`;
          }

          return (
            <source
              key={index}
              srcSet={item.srcSet}
              media={media}
            />
          );
        })}

        <img
          alt={alt || ''}
          className={classNames({
            [imgHideOnJavaScriptDisabled]: lazyLoad,
            [`${cssPrefix}__image`]: loadStyles,
            ...extraClassNames,
          })}
          height={pictureTagProps[0].height || undefined}
          src={pictureTagProps[0].src}
          width={pictureTagProps[0].width || undefined}
        />
      </picture>
    ) : (
      <>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <img
          alt={alt || ''}
          className={classNames({
            [imgHideOnJavaScriptDisabled]: lazyLoad,
            [`${cssPrefix}__image`]: loadStyles,
            ...extraClassNames,
          })}
          decoding={isLCPCandidate ? 'sync' : undefined}
          fetchpriority={isLCPCandidate ? 'high' : undefined}
          height={height || undefined}
          onClick={onClick || undefined}
          onKeyUp={onClick || undefined}
          role={role || undefined}
          src={src}
          srcSet={(srcSet && srcSet.length) ? srcSet : undefined}
          {...((srcSet && srcSet.length) ? { sizes: sizes || undefined } : '')}
          width={width || undefined}
          loading={lazyLoad ? 'lazy' : 'auto'}
        />

        {lazyLoad && (
          <noscript>
            <style>{`.${imgHideOnJavaScriptDisabled} { display: none !important; } `}</style>
            <img
              alt={alt || ''}
              className={classNames({
                [`${cssPrefix}__image`]: loadStyles,
                ...getClassesWithoutLazyLoad(extraClassNames),
              })}
              height={height || undefined}
              role={role || undefined}
              src={src}
              srcSet={(srcSet && srcSet.length) ? srcSet : undefined}
              {...((srcSet && srcSet.length) ? { sizes: sizes || undefined } : '')}
              width={width || undefined}
            />
          </noscript>
        )}
      </>
    );
  }

  amp() {
    return renderAmp.bind(this)();
  }

  renderImageTag() {
    const {
      caption,
      credits,
      displayImageMetadata,
      loadStyles,
      extraClassNames,
      context: { cssPrefix },
    } = this.props;

    if ((caption || credits) && displayImageMetadata) {
      return (
        <div
          className={classNames({
            [`${cssPrefix}__image--wrapper`]: loadStyles,
            ...extraClassNames,
          })}
        >
          <figure className={`${cssPrefix}__image--image`}>
            {this.imageTag}
          </figure>

          <div className={`${cssPrefix}__image--meta`}>
            {caption && <p className={`${cssPrefix}__image--caption`}>{caption}</p>}
            {/* eslint-disable-next-line react/no-danger */}
            {credits && <p className={`${cssPrefix}__image--credits`} dangerouslySetInnerHTML={{ __html: credits }} />}
          </div>
        </div>
      );
    }

    return this.imageTag;
  }

  renderBackgroundImageTag() {
    const {
      backgroundRatio, onClick, loadStyles, extraClassNames, context: { cssPrefix },
    } = this.props;
    const { src } = this.sizes;

    return (
      <div
        className={classNames({
          [`${cssPrefix}__image`]: loadStyles,
          [`${cssPrefix}__image--background`]: loadStyles,
          ...extraClassNames,
        })}
        style={{
          backgroundImage: `url(${src})`,
          paddingBottom: `${backgroundRatio}%`,
        }}
        onClick={onClick}
        role="presentation"
      />
    );
  }

  render() {
    const { backgroundImage } = this.props;

    return (
      backgroundImage ? this.renderBackgroundImageTag() : this.renderImageTag()
    );
  }
}

Image.propTypes = {
  alt: PropTypes.string,
  ampDensity: PropTypes.bool,
  ampDisableTransforms: PropTypes.bool,
  ampFixedMaxWidth: PropTypes.number,
  ampLayout: PropTypes.string,
  backgroundImage: PropTypes.bool,
  backgroundRatio: PropTypes.number,
  caption: PropTypes.string,
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
    imageSizesCallback: PropTypes.func,
    platform: PropTypes.string,
  }),
  credits: PropTypes.string,
  disableTransforms: PropTypes.bool,
  displayImageMetadata: PropTypes.bool,
  extraClassNames: PropTypes.instanceOf(Object),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string,
      size: PropTypes.string,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ),
  isLCPCandidate: PropTypes.bool,
  lazyLoad: PropTypes.bool,
  loadStyles: PropTypes.bool,
  onClick: PropTypes.func,
  renderPictureTag: PropTypes.bool,
  role: PropTypes.string,
  size: PropTypes.string,
  sizes: PropTypes.string,
  src: PropTypes.string,
  srcSet: PropTypes.instanceOf(Object),
  useStaticTransform: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

Image.defaultProps = {
  alt: '',
  ampDensity: false,
  ampDisableTransforms: false,
  ampFixedMaxWidth: 300,
  ampLayout: 'responsive',
  backgroundImage: false,
  backgroundRatio: 56.5,
  caption: '',
  context: {},
  credits: '',
  disableTransforms: false,
  displayImageMetadata: false,
  extraClassNames: {},
  height: '',
  images: [],
  isLCPCandidate: false,
  lazyLoad: true,
  loadStyles: true,
  onClick: () => {},
  renderPictureTag: false,
  role: '',
  size: '',
  sizes: '',
  src: '',
  srcSet: {},
  useStaticTransform: false,
  width: '',
};

export default compose(withPolaris, withAmp)(Image);
